import { Activity } from 'app/shared/features/activity-selector/models';
import { Batch } from 'app/shared/features/batch-selector/models';
import { Employee } from 'app/shared/features/employee-selector/models';
import { Locations } from 'app/shared/features/location-selector/models';

import {
    ErrorScannedCode,
    ScannedCode,
    ScannedCodeType,
    ScannedCodeWithEntity,
    SystemCode,
} from '../../../models';
import {
    getTimeRegistrationCodeTitle,
    isActivityMatchLocationType,
    isActivitySupportedForScanFlow,
    isSystemActivityCodeValid,
} from '../../../utils';
import { scannedCodeLocationTypeTitle, scannedCodeTitle } from './scanned-code-title.utils';

/*
 * This utility file handles single scanned code parsing, creation and validation
 * It differs from scanned-code-list.utils which handles list of scanned codes
 */

const regexDict: Record<ScannedCodeType, RegExp> = {
    activity: /^\s*a(?<code>\d+)\s*$|\b(login|logout|startbreak)\b/i,
    batch: /^\s*b(?<code>\d+)\s*$/i,
    employee: /^\s*e(?<code>\d+)\s*$/i,
    location: /^\s*l(?<code>\d+)\s*$/i,
    container: /^\s*c(?<code>\d+)\s*$/i,
    'site-identifier': /^([^;]*;){3}[^;]*$/i,
};

export const getScannedCodeRegex = (codeType: ScannedCodeType) => regexDict[codeType];

export const getShortScannedCode = (code: string): string => {
    return `${code.slice(0, 8)}${code.length > 8 ? '...' : ''}`;
};

const getCodeTypeFromScannedCode = (scannedCode: string) => {
    return (
        Object.keys(regexDict).find((key) => scannedCode.match(regexDict[key as ScannedCodeType])) ??
        undefined
    );
};

export function createScannedCodeFromActivity(activity: Activity): ScannedCodeWithEntity<Activity> {
    return {
        code: 'A' + activity.number,
        codeType: 'activity',
        entity: activity,
        number: activity.number,
        error: undefined,
        systemCode: createSystemCode(activity.name),
    };
}

export const createScannedCodeFromTimeRegistrationActivity = (
    activity: Activity,
): ScannedCodeWithEntity<Activity> => {
    return {
        code: activity.name,
        codeType: 'activity',
        entity: activity,
        number: 0,
        error: undefined,
        systemCode: createSystemCode(activity.name),
    };
};

export function createScannedCodeFromBatch(batch: Batch): ScannedCode {
    return {
        code: 'B' + batch.number,
        codeType: 'batch',
        number: batch.number,
        error: undefined,
    };
}

// Code there could be handled in system-activity
// It can either be login / logout / startbreak with specified code number in the registration type
export const createSystemCode = (code: string): SystemCode | undefined => {
    return isSystemActivityCodeValid(code)
        ? {
              isSystemCode: true,
              name: getTimeRegistrationCodeTitle(code),
          }
        : undefined;
};

export const parseScannedCode = ({
    code,
    allowedCodeTypes,
}: {
    code: string;
    allowedCodeTypes: ScannedCodeType[];
}): ScannedCode => {
    for (const codeType of allowedCodeTypes) {
        const match = code.match(regexDict[codeType]);
        if (match) {
            const timeRegistrationActivityNumber = 0;
            const matchedCodeNumber = match.groups?.code ? Number(match.groups.code) : undefined;
            const codeNumber = isSystemActivityCodeValid(code)
                ? timeRegistrationActivityNumber
                : matchedCodeNumber;

            return {
                number: codeNumber,
                codeType,
                code,
                error: undefined,
                systemCode: createSystemCode(code),
            };
        }
    }

    const scannedCodeType = getCodeTypeFromScannedCode(code) as ScannedCodeType;

    return {
        number: Number(getCodeTypeFromScannedCode(code)),
        codeType: scannedCodeType,
        code,
        error: {
            message: scannedCodeType
                ? `SCAN.ERROR.TITLE.EXPECTED_CODE_BUT_WRONG_CODE_SCANNED`
                : 'SCAN.ERROR.TITLE.EXPECTED_CODE_BUT_UNKNOWN_CODE_SCANNED',
            expectedCodeType: allowedCodeTypes,
            scannedCodeType: scannedCodeType ? scannedCodeTitle[scannedCodeType] : undefined,
            shortScannedValue: getShortScannedCode(code),
        },
    };
};

export function getScanErrorEmployee(
    employee: Employee | undefined,
    codeType: ScannedCodeType,
    scannedCode: string,
): { error: ErrorScannedCode | undefined } {
    if (!employee)
        return {
            error: {
                message: 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED',
                scannedCodeType: scannedCodeTitle[codeType],
                shortScannedValue: getShortScannedCode(scannedCode),
            },
        };
    return { error: undefined };
}

export function getScanErrorLocation(
    location: Locations | undefined,
    currentActivity: Activity | undefined,
    codeType: ScannedCodeType,
    scannedCode: string,
): { error: ErrorScannedCode | undefined } {
    if (!location) {
        return {
            error: {
                message: 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED',
                scannedCodeType: scannedCodeTitle[codeType],
                shortScannedValue: getShortScannedCode(scannedCode),
            },
        };
    }

    if (currentActivity && !isActivityMatchLocationType(currentActivity, location.locationType)) {
        const expectedLocationType =
            currentActivity.locationType !== null
                ? scannedCodeLocationTypeTitle[currentActivity.locationType]
                : undefined;
        const scannedLocationType = scannedCodeLocationTypeTitle[location.locationType] ?? undefined;
        return {
            error: {
                message: 'SCAN.ERROR.TITLE.LOCATION_TYPE_EXPECTED_BUT_OTHER_LOCATION_TYPE_SCANNED',
                expectedCodeType: expectedLocationType,
                scannedCodeType: scannedLocationType,
            },
        };
    }
    return { error: undefined };
}

export function getScanErrorActivity(
    activity: Activity | undefined,
    codeType: ScannedCodeType,
    scannedCode: string,
): { error: ErrorScannedCode | undefined } {
    if (!activity) {
        return {
            error: {
                message: 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED',
                scannedCodeType: scannedCodeTitle[codeType],
                shortScannedValue: getShortScannedCode(scannedCode),
            },
        };
    } else if (!isActivitySupportedForScanFlow(activity)) {
        return {
            error: {
                message: `SCAN.ERROR.TITLE.ACTIVITY_NOT_SUPPORTED`,
                shortScannedValue: activity.name,
            },
        };
    }
    return { error: undefined };
}
